/**
 * Design tokens — the ADMIN dialect's density scale (BL-1357).
 *
 * ADR 030 — Design Canon assigns "density" to the admin dialect (Layer 2) and,
 * until this file, never defined it: every component author picked `gap-1` vs
 * `gap-3` by feel, and a reviewer who found a row cramped had nothing to cite.
 * These are the named tiers. Each is a step on `spacing.ts` — the type below is
 * what makes an off-scale value (`10`, `6`) a compile error rather than a
 * judgement call — and each is generated into `apps/admin-web` as a
 * `--density-*` CSS var by `renderAdminDensityCssVars` in `./css.ts`, then
 * bound to Tailwind utilities (`gap-density-control`, `py-density-row-y`, …) in
 * admin-web's `tailwind.config.ts`.
 *
 * **Admin only, on purpose.** This is a dialect, not foundation: it is rendered
 * to its own generated file, never into the shared `generated-tokens.css` both
 * apps import, and bidder-web's Tailwind config never learns these keys.
 * bidder-web's `--bw-*` vocabulary is the other dialect, and "unifying" the two
 * is the regression the Canon names. The rule file is
 * `.claude/rules/ui-standards.md` § Spacing; the enforcement gate is
 * `density/no-raw-gap` (`.claude/rules/design-system-enforcement.md`).
 */

import { spacing } from './spacing';

/** A value on the spacing scale. Anything else fails to compile. */
export type SpacingStep = (typeof spacing)[keyof typeof spacing];

/**
 * The admin dialect's density tiers, in px, largest rhythm first.
 *
 * | Tier         | Use                                                      |
 * |--------------|----------------------------------------------------------|
 * | `page`       | page padding (`p-density-page`)                          |
 * | `sectionY`   | rhythm between sections (`space-y-density-section`)      |
 * | `groupGap`   | form fields / sibling groups (`space-y-density-group`)   |
 * | `controlGap` | a value and its adjacent control, a row's left/right split |
 * | `clusterGap` | items that read as one thing: label + badge, name + paddle |
 * | `tightGap`   | an icon glued to its own label                           |
 * | `rowY/rowX`  | list / table row and cell padding                        |
 */
export const adminDensity = {
    page: spacing[32],
    sectionY: spacing[24],
    groupGap: spacing[16],
    // 12px, not 8: the clerk console's bid-record amount ↔ ⋮ gap shipped at
    // 12 in PR #4055, slid back to 8 in BL-1325, and was re-pinned at 12 by
    // Dan on 2026-09-10 (BL-1357 RAC 4). The token is what stops the slide.
    controlGap: spacing[12],
    clusterGap: spacing[8],
    tightGap: spacing[4],
    rowY: spacing[8],
    rowX: spacing[12],
} as const satisfies Record<string, SpacingStep>;

/** The shape `renderAdminDensityCssVars` reads — widened so tests can substitute a tier. */
export type AdminDensityTokens = { [K in keyof typeof adminDensity]: SpacingStep };
